(() => {
  if (window.EasyMeepleBoxSynchronizer) return;

  const configNode = document.getElementById('EasyMeepleBoxConfig');
  if (!configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    console.error('Could not read the EasyMeeple Box configuration.', error);
    return;
  }

  config.boxProductId = String(config.boxProductId || '');
  config.boxVariants = Object.fromEntries(
    Object.entries(config.boxVariants || {})
      .filter(([, variantId]) => variantId)
      .map(([count, variantId]) => [String(count), String(variantId)])
  );
  config.boxItemsCollectionUrl = String(config.boxItemsCollectionUrl || '');
  config.boxItemProductIds = (config.boxItemProductIds || []).filter(Boolean).map(String);
  config.maxGames = Number(config.maxGames) || 5;

  if (!config.boxProductId || !Object.keys(config.boxVariants).length || !config.boxItemsCollectionUrl) return;

  class EasyMeepleBoxSynchronizer {
    constructor(settings) {
      this.config = settings;
      this.boxItemProductIds = new Set(settings.boxItemProductIds);
      this.queue = Promise.resolve();
      this.pending = false;
      this.clearing = false;
      this.clearPromise = null;
      this.ready = false;
      this.lastState = null;
      this.unsubscribeCart = null;
      this.handlePageShow = () => this.requestSync('pageshow');
      this.handleCustomCartUpdate = () => this.requestSync('custom-cart-update');
    }

    async initialize() {
      await this.loadBoxItemProductIds();
      this.ready = true;
      this.bindEvents();
      await this.requestSync('initial');
    }

    bindEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'easymeeple-box-sync') return;
          this.requestSync('cart-update');
        });
      }

      document.addEventListener('easymeeple:cart-updated', this.handleCustomCartUpdate);
      window.addEventListener('pageshow', this.handlePageShow);
    }

    async loadBoxItemProductIds() {
      const pageSize = 250;
      let page = 1;

      try {
        while (page <= 20) {
          const url = new URL(
            `${this.config.boxItemsCollectionUrl.replace(/\/$/, '')}/products.json`,
            window.location.origin
          );
          url.searchParams.set('limit', String(pageSize));
          url.searchParams.set('page', String(page));

          const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('Could not load the Box items collection');

          const data = await response.json();
          const products = Array.isArray(data.products) ? data.products : [];
          const previousSize = this.boxItemProductIds.size;
          products.forEach((product) => this.boxItemProductIds.add(String(product.id)));

          if (products.length < pageSize || this.boxItemProductIds.size === previousSize) break;
          page += 1;
        }
      } catch (error) {
        console.error('Could not refresh the EasyMeeple Box product lookup.', error);
      }
    }

    requestSync(reason = 'manual') {
      if (this.clearing) {
        return Promise.resolve(
          this.lastState ? { cart: this.lastState.cart, state: this.lastState, changed: false } : null
        );
      }

      this.pending = true;
      this.queue = this.queue
        .catch(() => undefined)
        .then(async () => {
          let result = this.lastState;
          while (this.pending) {
            this.pending = false;
            result = await this.synchronize(reason);
          }
          return result;
        });
      return this.queue;
    }

    clearCart() {
      if (this.clearPromise) return this.clearPromise;

      this.clearPromise = this.performClearCart().finally(() => {
        this.clearPromise = null;
      });

      return this.clearPromise;
    }

    pauseForCartClear() {
      this.clearing = true;
      this.pending = false;
    }

    resumeAfterCartClear() {
      this.clearing = false;
      this.requestSync('clear-cart-failed');
    }

    async performClearCart() {
      this.clearing = true;
      this.pending = false;

      try {
        // Let an already-running synchronization finish before the atomic clear.
        await this.queue.catch(() => undefined);

        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({}),
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          throw new Error(cart.description || cart.message || 'Could not clear the Shopify cart');
        }

        window.EasyMeepleAnalytics?.cart(cart, item => this.isGame(item));
      const state = this.summarize(cart);
        this.lastState = state;
        await this.refreshCartSurfaces(cart);

        document.dispatchEvent(
          new CustomEvent('easymeeple:box-synced', {
            detail: { cart, state, changed: true, reason: 'cart-cleared' },
          })
        );
        document.dispatchEvent(
          new CustomEvent('easymeeple:cart-cleared', { detail: { cart, state } })
        );

        return { cart, state, changed: true };
      } finally {
        this.clearing = false;
      }
    }

    async synchronize(reason) {
      const cart = await this.fetchCart();
      window.EasyMeepleAnalytics?.cart(cart, item => this.isGame(item));
      const state = this.summarize(cart);
      let changed = false;

      if (!state.isSynchronized) {
        changed = await this.applyDesiredBox(state);
      }

      const finalCart = changed ? await this.fetchCart() : cart;
      const finalState = this.summarize(finalCart);
      this.lastState = finalState;

      if (changed) {
        await this.refreshCartSurfaces(finalCart);
      }

      document.dispatchEvent(
        new CustomEvent('easymeeple:box-synced', {
          detail: { cart: finalCart, state: finalState, changed, reason },
        })
      );

      return { cart: finalCart, state: finalState, changed };
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Could not load the Shopify cart');
      return response.json();
    }

    isGame(item) {
      return this.boxItemProductIds.has(String(item.product_id));
    }

    summarize(cart) {
      const gameItems = [];
      const boxItems = [];
      let gameCount = 0;

      cart.items.forEach((item) => {
        if (this.isGame(item)) {
          gameItems.push(item);
          gameCount += Number(item.quantity) || 0;
        }
        if (String(item.product_id) === this.config.boxProductId) boxItems.push(item);
      });

      const desiredVariantId =
        gameCount >= 1 && gameCount <= this.config.maxGames
          ? String(this.config.boxVariants[String(gameCount)] || '')
          : '';
      const correctLines = desiredVariantId
        ? boxItems.filter(
            (item) => String(item.variant_id) === desiredVariantId && Number(item.quantity) === 1
          )
        : [];
      const isSynchronized = desiredVariantId
        ? boxItems.length === 1 && correctLines.length === 1
        : boxItems.length === 0;

      return {
        cart,
        gameItems,
        boxItems,
        gameCount,
        desiredVariantId,
        isSynchronized,
        validGameCount: gameCount >= 1 && gameCount <= this.config.maxGames,
      };
    }

    async applyDesiredBox(state) {
      const updates = {};
      let keepDesiredLine = false;

      state.boxItems.forEach((item) => {
        const isDesired =
          state.desiredVariantId &&
          String(item.variant_id) === state.desiredVariantId &&
          Number(item.quantity) === 1 &&
          !keepDesiredLine;

        if (isDesired) keepDesiredLine = true;
        else updates[item.key] = 0;
      });

      if (Object.keys(updates).length) {
        await this.updateCart(updates);
      }

      if (state.desiredVariantId && !keepDesiredLine) {
        await this.addBoxVariant(state.desiredVariantId);
      }

      return Object.keys(updates).length > 0 || Boolean(state.desiredVariantId && !keepDesiredLine);
    }

    async updateCart(updates) {
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ updates }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not update the EasyMeeple Box');
      }
      return result;
    }

    async addBoxVariant(variantId) {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: { _easymeeple_box_managed: 'true' },
            },
          ],
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add the EasyMeeple Box');
      }
      return result;
    }

    async refreshCartSurfaces(cart) {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sectionIds = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || ['cart-icon-bubble'];
      if (!sectionIds.length) return;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const params = new URLSearchParams({ sections: [...new Set(sectionIds)].join(',') });
        const response = await fetch(`${root}?${params.toString()}`, {
          headers: { Accept: 'application/json' },
        });
        const sections = await response.json();
        if (!response.ok) return;

        if (cartDrawer?.getSectionsToRender) {
          cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
          cartDrawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', cart.item_count === 0);
          cartDrawer.getSectionsToRender().forEach((section) => {
            if (typeof sections?.[section.id] !== 'string') return;
            const target = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!target) return;
            target.innerHTML = cartDrawer.getSectionInnerHTML(sections[section.id], section.selector);
          });
        } else if (typeof sections?.['cart-icon-bubble'] === 'string') {
          const parser = new DOMParser();
          const html = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const replacement = html.querySelector('#cart-icon-bubble');
          const current = document.querySelector('#cart-icon-bubble');
          if (replacement && current) current.replaceWith(replacement);
        }
      } catch (error) {
        console.warn('Could not refresh cart surfaces after synchronizing the EasyMeeple Box.', error);
      }
    }
  }

  const synchronizer = new EasyMeepleBoxSynchronizer(config);
  window.EasyMeepleBoxSynchronizer = synchronizer;

  const start = () => {
    synchronizer.initialize().catch((error) => {
      console.error('Could not initialize EasyMeeple Box synchronization.', error);
      document.dispatchEvent(
        new CustomEvent('easymeeple:box-sync-error', { detail: { error } })
      );
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

